import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Row, Col, Button, Progress, Alert, Input, Badge, Card, CardBody, CardHeader, Pagination, PaginationItem, PaginationLink
} from "reactstrap";
import { TailSpin } from "react-loader-spinner";
import { Enviroments } from "../enviroments/enviroments";

/* -------------------- Tema / helpers -------------------- */
const COLORS = {
  bg: "#0B0B0F",
  surface: "#121317",
  border: "#26272B",
  text: "#ECECF3",
  sub: "#A3A6AF",
  primary: "#913ef8",
  success: "#2ecc71",
  danger: "#e74c3c",
};

const Shimmer = ({ width = "100%", height = 16, radius = 8, className = "" }) => (
  <div
    className={className}
    style={{
      width,
      height,
      borderRadius: radius,
      background:
        "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 37%, rgba(255,255,255,0.06) 63%)",
      backgroundSize: "400% 100%",
      animation: "shimmer 1.2s ease-in-out infinite"
    }}
  />
);

const GlobalStyles = () => (
  <style>{`
    @keyframes shimmer {
      0% { background-position: 100% 0; }
      100% { background-position: -100% 0; }
    }
    .game-card {
      position: relative;
      border-radius: 10px;
      overflow: hidden;
      background: #1a1b20;
      border: 1px solid ${COLORS.border};
      cursor: pointer;
      min-height: 120px;
    }
    .game-card__bg {
      position: absolute; inset: 0;
      background-size: cover; background-position: center;
      filter: brightness(0.9);
      transition: transform .25s ease;
    }
    .game-card:hover .game-card__bg { transform: scale(1.03); }
    .game-card__overlay {
      position: absolute; inset: 0;
      background: linear-gradient(0deg, rgba(0,0,0,0.65), rgba(0,0,0,0.05));
      display: flex; flex-direction: column; justify-content: flex-end;
      padding: 10px;
    }
    .game-card__title {
      color: ${COLORS.text};
      font-size: 12px; line-height: 1.2;
      margin: 0 0 8px 0;
      text-shadow: 0 1px 2px rgba(0,0,0,.6);
    }
    .game-card__cta {
      align-self: flex-start;
      padding: 4px 10px;
      border-radius: 999px;
      background: ${COLORS.primary};
      color: #fff; font-weight: 700; font-size: 11px; border: none;
    }
    .toolbar-card {
      background: ${COLORS.surface}; border: 1px solid ${COLORS.border};
    }
  `}</style>
);

/* -------------------- Component -------------------- */

const GamesSignal = () => {
  const [categories, setCategories] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iframeLink, setIframeLink] = useState(null);
  const [iframeTitle, setIframeTitle] = useState("");
  const [htmlContent, setHtmlContent] = useState(null);
  const [error, setError] = useState("");
  const [categorySelected, setCategorySelected] = useState("");

  // sinais
  const [signalMessage, setSignalMessage] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [countdownDuration, setCountdownDuration] = useState(60);
  const [signalRoleta, setSignalRoleta] = useState(null);

  // busca
  const [search, setSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]); // lista plana
  const [searchError, setSearchError] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 12 });

  const isMobile = useMemo(() => window.innerWidth <= 768, []);
  const apiToken = useMemo(() => localStorage.getItem("apiToken"), []);

  const buildImageUrl = useCallback((raw) => {
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    // fallback: CDN padrão
    return `https://images.cometagaming.com/${raw}/${isMobile ? "mobile" : "desktop"}`;
  }, [isMobile]);

  /* ---------- categorias (padrão) ---------- */
  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await fetch(
          `${Enviroments.API_URL_LOTOGREEN}v2/casino-games/category`,
          { headers: { Authorization: `Bearer ${apiToken}` } }
        );
        const data = await resp.json();

        if (abort) return;

        const first = data.find((i) => i.position === 9);
        const rest = data.filter((i) => i.position !== 9);
        const sorted = first ? [first, ...rest] : data;

        setCategories(sorted);
        setFiltered(sorted);
      } catch (e) {
        if (!abort) {
          setError("Estamos com problemas para carregar os jogos agora.");
          console.error("Erro ao carregar categorias:", e);
        }
      } finally {
        if (!abort) setLoading(false);
      }
    })();

    return () => { abort = true; };
  }, [apiToken]);

  /* ---------- countdown ---------- */
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  /* ---------- busca remota (API de search) com debounce ---------- */
  useEffect(() => {
    // quando não há texto, limpa resultados remotos e volta às categorias
    if (!search.trim()) {
      setSearchResults([]);
      setSearchError("");
      setSearchLoading(false);
      setPage(1);
      return;
    }

    const controller = new AbortController();
    const tid = setTimeout(async () => {
      try {
        setSearchLoading(true);
        setSearchError("");
        const url = `https://lotogreen.bet.br/api/casino-games?page=${page}&limit=${meta?.per_page || 12}&search=${encodeURIComponent(
          search.trim()
        )}&order=clicks`;
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${apiToken}` },
          signal: controller.signal
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();

        setSearchResults(json?.data || []);
        setMeta(json?.meta || { current_page: page, last_page: page, total: (json?.data || []).length, per_page: 12 });
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Erro na busca:", e);
          setSearchError("Não foi possível buscar agora. Tente novamente.");
        }
      } finally {
        setSearchLoading(false);
      }
    }, 400); // debounce

    return () => {
      clearTimeout(tid);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, apiToken]);

  /* ---------- reset de página ao trocar termo ---------- */
  useEffect(() => {
    setPage(1);
  }, [search]);

  /* ---------- helper sinais ---------- */
  const getRandomEntries = () => {
    const rnd = () => Math.floor(Math.random() * (18 - 5 + 1)) + 5;
    return { normalEntries: rnd(), turboEntries: rnd() };
  };

  const generateSignalSlots = useCallback(() => {
    setShowLoader(true);
    setSignalRoleta(null);
    setCountdownDuration(60);
    setTimeout(() => {
      const { normalEntries, turboEntries } = getRandomEntries();
      setSignalMessage(
        `Fazer ${normalEntries} entradas normal e ${turboEntries} entradas no turbo`
      );
      setCountdown(60);
      setShowLoader(false);
    }, 1500);
  }, []);

  const generateSignalRoleta = useCallback(() => {
    setShowLoader(true);
    setSignalMessage("");
    setCountdownDuration(120);

    const numbers = Array.from({ length: 5 }, () => Math.floor(Math.random() * 36));
    const sections = ["Vermelho", "Preto", "Orphelins", "Tiers du Cylindre", "Voisins du Zéro"];
    const columns = ["1ª Coluna", "2ª Coluna", "3ª Coluna"];

    setTimeout(() => {
      setSignalRoleta({
        mainNumber: numbers[0],
        coverage: numbers.slice(1),
        section: sections[Math.floor(Math.random() * sections.length)],
        columnCoverage: [
          columns[Math.floor(Math.random() * columns.length)],
          columns[Math.floor(Math.random() * columns.length)]
        ],
      });
      setCountdown(120);
      setShowLoader(false);
    }, 1500);
  }, []);

  /* ---------- abrir jogo ---------- */
  const handleGameClick = async (gameId, gameName, categoryName) => {
    try {
      setIframeLink(null);
      setIframeTitle("");
      setHtmlContent(null);
      setSignalMessage("");
      setSignalRoleta(null);
      setCountdown(0);

      const resp = await fetch(
        `${Enviroments.API_URL_LOTOGREEN}api/casino-games/${gameId}/start?demo=0&isMobileDevice=${isMobile ? 1 : 0}`,
        { headers: { Authorization: `Bearer ${apiToken}` } }
      );
      const data = await resp.json();
      setCategorySelected(categoryName || "");

      if (typeof data.link === "string" && (data.link.startsWith("<!DOCTYPE html>") || data.link.startsWith("<!doctype html>"))) {
        setIframeTitle(gameName);
        setHtmlContent(data.link);
      } else {
        setIframeTitle(gameName);
        setIframeLink(data.link);
      }
    } catch (e) {
      console.error("Erro ao iniciar o jogo:", e);
      setError("Não foi possível iniciar este jogo agora.");
    }
  };

  const handleBack = () => {
    setIframeLink(null);
    setIframeTitle("");
    setHtmlContent(null);
    setSignalMessage("");
    setCountdown(0);
    setSignalRoleta(null);
  };

  /* ---------- UI sinais reutilizável ---------- */
  const SignalPanel = ({ type }) => {
    const isSlots = type === "slots";
    const hasSignal = isSlots ? !!signalMessage : !!signalRoleta;
    const onClick = isSlots ? generateSignalSlots : generateSignalRoleta;
    const title = isSlots ? "Sinal para Slots" : "Sinal para Roleta";
    const subtitle = isSlots
      ? "Clique para receber uma recomendação de entradas normal e turbo."
      : "Clique para receber recomendação de número/colunas e cobertura.";
    const waitingLabel = countdown > 0 ? "Aguarde para solicitar novamente" : "Solicitar Sinal";

    return (
      <Card style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }} className="mb-3">
        <CardHeader className="pb-1">
          <h5 style={{ color: COLORS.text, marginBottom: 4 }}>{title}</h5>
          <div style={{ color: COLORS.sub, fontSize: 12 }}>{subtitle}</div>
        </CardHeader>
        <CardBody>
          <div className="text-center">
            <Button
              color="primary"
              disabled={countdown > 0 || showLoader}
              onClick={onClick}
              style={{ minWidth: 180, background: COLORS.primary, border: "none" }}
            >
              {showLoader ? "Gerando…" : waitingLabel}
            </Button>

            {showLoader && (
              <div className="mt-2 d-flex justify-content-center">
                <TailSpin height={24} width={24} color="#ffffff" />
              </div>
            )}

            {hasSignal && (
              <div className="mt-3">
                {isSlots ? (
                  <Alert color="success" style={{ background: "#133018", border: "1px solid #1f6d37", color: "#c7f0d2" }}>
                    {signalMessage}
                  </Alert>
                ) : (
                  <div style={{ color: COLORS.text }}>
                    <h4 className="text-success" style={{ color: COLORS.success, fontWeight: 800 }}>SINAL CONFIRMADO</h4>
                    <p>🎯 Apostar no número <strong style={{ fontSize: 20 }}>{signalRoleta.mainNumber}</strong></p>
                    <p>Race: <strong>6 ou 7</strong></p>
                    <p>Colunas sugeridas: <strong>{signalRoleta.columnCoverage.join(" e ")}</strong></p>
                    <p>Proteção no número: <strong>0</strong></p>
                    <p>Fazer até 3 entradas. (Gale Opcional)</p>
                  </div>
                )}
              </div>
            )}

            {countdown > 0 && (
              <div className="mt-2">
                <div style={{ color: COLORS.sub, marginBottom: 6 }}>
                  Tempo para próximo sinal: {Math.floor(countdown / 60)}:
                  {(countdown % 60).toString().padStart(2, "0")}
                </div>
                <Progress value={((countdownDuration - countdown) / countdownDuration) * 100} />
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  /* ---------- Views de detalhe ---------- */

  if (loading) {
    return (
      <div className="content">
        <GlobalStyles />
        <Card className="toolbar-card mb-3">
          <CardBody className="py-3">
            <Row>
              <Col md="6" className="mb-2">
                <Shimmer width="60%" height={18} />
              </Col>
            </Row>
          </CardBody>
        </Card>
        <Row>
          {Array.from({ length: 10 }).map((_, i) => (
            <Col key={i} xs="6" sm="4" md="3" lg="2" className="mb-3">
              <Shimmer height={120} />
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (htmlContent) {
    return (
      <div className="content">
        <GlobalStyles />
        <Button color="link" className="p-0 mb-2" onClick={handleBack} style={{ color: COLORS.text }}>
          <i className="fa fa-chevron-left mr-1" /> Voltar
        </Button>

        <h4 className="mb-3" style={{ color: COLORS.text }}>{iframeTitle}</h4>

        {(categorySelected === "Jogos da PG" || categorySelected === "Melhores TaDa Gaming") && (
          <SignalPanel type="slots" />
        )}

        <iframe
          className="img-fluid"
          frameBorder="0"
          srcDoc={htmlContent}
          title={iframeTitle}
          style={{ width: "100%", height: "80vh", border: "none" }}
        />
      </div>
    );
  }

  if (iframeLink) {
    return (
      <div className="content">
        <GlobalStyles />
        <Button color="link" className="p-0 mb-2" onClick={handleBack} style={{ color: COLORS.text }}>
          <i className="fa fa-chevron-left mr-1" /> Voltar
        </Button>

        <h4 className="mb-3" style={{ color: COLORS.text }}>{iframeTitle}</h4>

        {categorySelected === "Jogos de Roleta" && <SignalPanel type="roleta" />}

        <iframe
          src={iframeLink}
          title={iframeTitle}
          style={{ width: "100%", height: "80vh", border: "none" }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  /* ---------- Lista principal (categorias ou resultados da busca) ---------- */
  const isSearching = !!search.trim();
  const listToRender = isSearching ? searchResults : filtered;

  return (
    <div className="content">
      <GlobalStyles />

      {/* Toolbar */}
      <Card className="toolbar-card mb-3">
        <CardBody className="py-3">
          <Row className="align-items-end">
            <Col md="6" className="mb-2">
              <div style={{ color: COLORS.sub, fontSize: 12, marginBottom: 6 }}>Buscar jogo</div>
              <Input
                placeholder="Digite o nome do jogo…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ background: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}` }}
              />
              {searchLoading && (
                <div className="mt-2 d-flex align-items-center" style={{ color: COLORS.sub, gap: 8 }}>
                  <TailSpin height={16} width={16} color="#ffffff" /> Buscando…
                </div>
              )}
            </Col>
            <Col md="6" className="mb-2 d-flex justify-content-md-end">
              <div className="d-flex align-items-center" style={{ gap: 10, color: COLORS.sub }}>
                <Badge pill style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.sub }}>
                  {isSearching
                    ? (meta?.total ?? listToRender.length)
                    : filtered.reduce((acc, c) => acc + (c.games?.length || 0), 0)
                  } jogos
                </Badge>
                {!isSearching && (
                  <Badge pill style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, color: COLORS.sub }}>
                    {filtered.length} categorias
                  </Badge>
                )}
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {(error || searchError) && (
        <Alert color="danger" style={{ background: "#2a1111", border: `1px solid ${COLORS.border}`, color: "#f5c2c7" }}>
          {error || searchError}
        </Alert>
      )}

      {/* Resultados de busca remota */}
      {isSearching ? (
        <>
          <h4 className="mb-3" style={{ color: COLORS.text }}>
            Resultados para: “{search}”
          </h4>
          {searchResults.length === 0 && !searchLoading ? (
            <div style={{ color: COLORS.sub }}>Nenhum jogo encontrado.</div>
          ) : (
            <Row>
              {searchResults.map((game) => {
                const bgUrl = buildImageUrl(game.image);
                return (
                  <Col xs="6" sm="4" md="3" lg="2" key={game.id} className="mb-3">
                    <div
                      className="game-card"
                      role="button"
                      title={game.name}
                      onClick={() => handleGameClick(game.id, game.name, game.provider?.name || "")}
                    >
                      <div className="game-card__bg" style={{ backgroundImage: `url(${bgUrl})` }} />
                      <div className="game-card__overlay">
                        <div className="game-card__title">
                          {game.name}
                          {game?.provider?.name && (
                            <div style={{ fontSize: 10, color: COLORS.sub }}>{game.provider.name}</div>
                          )}
                        </div>
                        <button className="game-card__cta">Abrir</button>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}

          {/* Paginação da busca */}
          {meta?.last_page > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <PaginationItem disabled={page <= 1}>
                  <PaginationLink previous onClick={() => setPage((p) => Math.max(1, p - 1))} />
                </PaginationItem>
                {Array.from({ length: meta.last_page }).map((_, i) => (
                  <PaginationItem key={i} active={page === i + 1}>
                    <PaginationLink onClick={() => setPage(i + 1)}>
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={page >= meta.last_page}>
                  <PaginationLink next onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))} />
                </PaginationItem>
              </Pagination>
            </div>
          )}
        </>
      ) : (
        /* Listagem por categorias (padrão) */
        filtered.map((category) => (
          <div key={category.id} className="mb-4">
            <h4 className="mb-3" style={{ color: COLORS.text }}>{category.name}</h4>
            <Row>
              {(category.games || []).map((game) => {
                const bgUrl = buildImageUrl(game.image);
                return (
                  <Col xs="6" sm="4" md="3" lg="2" key={game.id} className="mb-3">
                    <div
                      className="game-card"
                      role="button"
                      title={game.name}
                      onClick={() => handleGameClick(game.id, game.name, category.name)}
                    >
                      <div className="game-card__bg" style={{ backgroundImage: `url(${bgUrl})` }} />
                      <div className="game-card__overlay">
                        <div className="game-card__title">{game.name}</div>
                        <button className="game-card__cta">Abrir</button>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        ))
      )}
    </div>
  );
};

export default GamesSignal;
